import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Check, Zap, Sparkles, Shield } from 'lucide-react';
import { saasApi } from '@/services/api';
import type { SaaSPackage } from '@/types';
import { formatMoney } from '@/utils/currency';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { getCyclePackageCode, groupPackages } from '@/utils/packageGroup';

export type SelectedAddonPayload = {
  addon_code: string;
  name: string;
  amount: number;
};

interface PackagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan?: (
    pkgName: string,
    amount: number,
    cycle: 'monthly' | 'quarterly' | 'annual',
    selectedAddons: SelectedAddonPayload[],
    packageCode: string,
  ) => void;
}

function addonKey(addon: { addon_code?: string; addon?: string; name?: string }) {
  return addon.addon_code || addon.addon || addon.name || '';
}

export default function PackagesModal({ isOpen, onClose, onSelectPlan }: PackagesModalProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  /** Per-package selected addon codes */
  const [selectedByPackage, setSelectedByPackage] = useState<Record<string, string[]>>({});
  useEscapeToClose(onClose, isOpen);

  const { data: packagesData, isLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      const res = await saasApi.getPackages();
      return res.data.message as SaaSPackage[];
    },
    enabled: isOpen,
  });

  const packages = useMemo(
    () => (packagesData ? groupPackages(packagesData) : []),
    [packagesData],
  );

  const visiblePackages = packages.filter((pkg) => {
    if (pkg.is_custom_price) return true;
    if (billingCycle === 'monthly') return Boolean(pkg.monthly_package_code);
    if (billingCycle === 'quarterly') return Boolean(pkg.quarterly_package_code);
    if (billingCycle === 'annual') return Boolean(pkg.annual_package_code);
    return false;
  });

  const toggleAddOn = (pkgKey: string, code: string) => {
    setSelectedByPackage((prev) => {
      const cur = prev[pkgKey] || [];
      return {
        ...prev,
        [pkgKey]: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code],
      };
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Available Packages</h2>
            <p className="text-sm text-gray-500">Choose a tier, billing cycle, and optional add-ons</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-8">
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-1 inline-flex">
                  {(
                    [
                      ['monthly', 'Monthly'] as const,
                      ...(packages.some((p) => p.quarterly_package_code)
                        ? [['quarterly', 'Quarterly'] as const]
                        : []),
                      ...(packages.some((p) => p.annual_package_code)
                        ? [['annual', 'Annual'] as const]
                        : []),
                    ] as const
                  ).map(([cycle, label]) => (
                    <button
                      key={cycle}
                      type="button"
                      onClick={() => setBillingCycle(cycle)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        billingCycle === cycle
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {visiblePackages.map((pkg) => {
                  const pkgKey = pkg.package_name;
                  const selectedCodes = selectedByPackage[pkgKey] || [];
                  const purchasable = (pkg.add_ons || []).filter((a: any) => !Number(a.included));
                  const included = (pkg.add_ons || []).filter((a: any) => Number(a.included));

                  return (
                    <div
                      key={pkgKey}
                      className="card p-6 border border-gray-200 shadow-sm hover:border-primary-200 transition-all flex flex-col"
                    >
                      <div className="text-center mb-6">
                        <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                          {pkg.package_name.toLowerCase().includes('basic') ||
                          pkg.package_name.toLowerCase().includes('starter') ? (
                            <Zap className="w-6 h-6 text-primary-600" />
                          ) : pkg.package_name.toLowerCase().includes('business') ||
                            pkg.package_name.toLowerCase().includes('medium') ? (
                            <Sparkles className="w-6 h-6 text-primary-600" />
                          ) : (
                            <Shield className="w-6 h-6 text-primary-600" />
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg">{pkg.package_name}</h3>
                      </div>

                      <div className="text-center mb-6 pb-6 border-b border-gray-100">
                        {pkg.is_custom_price ? (
                          <span className="text-3xl font-black text-gray-900">Custom</span>
                        ) : (
                          <>
                            <span className="text-3xl font-black text-gray-900">
                              {formatMoney(
                                billingCycle === 'annual'
                                  ? pkg.annual_price || 0
                                  : billingCycle === 'quarterly'
                                    ? pkg.quarterly_price || 0
                                    : pkg.monthly_price || 0,
                              )}
                            </span>
                            <span className="text-gray-500 text-sm font-medium ml-1">
                              /
                              {billingCycle === 'annual'
                                ? 'year'
                                : billingCycle === 'quarterly'
                                  ? 'quarter'
                                  : 'mo'}
                            </span>
                          </>
                        )}
                      </div>

                      <ul className="space-y-3 mb-4 text-sm text-gray-600">
                        <li className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-emerald-500 shrink-0" />
                          <span>{pkg.max_users ? `Up to ${pkg.max_users} users` : 'Unlimited users'}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-emerald-500 shrink-0" />
                          <span>
                            {pkg.max_products
                              ? `${pkg.max_products.toLocaleString()} products`
                              : 'Unlimited products'}
                          </span>
                        </li>
                        {included.map((a: any) => (
                          <li key={addonKey(a)} className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-emerald-500 shrink-0" />
                            <span>
                              {a.add_on} <span className="text-xs text-emerald-700">(included)</span>
                            </span>
                          </li>
                        ))}
                      </ul>

                      {purchasable.length > 0 && (
                        <div className="mb-6 border-t border-gray-100 pt-4">
                          <p className="text-xs font-semibold text-gray-700 mb-3 text-left">Optional add-ons</p>
                          <div className="space-y-2">
                            {purchasable.map((addon: any) => {
                              const code = addonKey(addon);
                              const checked = selectedCodes.includes(code);
                              return (
                                <label
                                  key={code}
                                  className={`flex items-start gap-2 border rounded-lg p-2 cursor-pointer transition-all ${
                                    checked
                                      ? 'border-primary-600 bg-primary-50'
                                      : 'border-gray-200 bg-white hover:border-primary-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    checked={checked}
                                    onChange={() => toggleAddOn(pkgKey, code)}
                                  />
                                  <div className="flex-1 min-w-0 text-left">
                                    <div className="flex justify-between gap-1">
                                      <span className="text-xs font-medium text-gray-900">{addon.add_on}</span>
                                      <span className="text-xs font-semibold text-gray-900 shrink-0">
                                        +{formatMoney(addon.amount || 0)}
                                      </span>
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {onSelectPlan && (
                        <button
                          type="button"
                          onClick={() => {
                            let displayPrice = 0;
                            if (!pkg.is_custom_price) {
                              if (billingCycle === 'annual') displayPrice = pkg.annual_price || 0;
                              else if (billingCycle === 'quarterly') displayPrice = pkg.quarterly_price || 0;
                              else displayPrice = pkg.monthly_price || 0;
                            }
                            const selectedAddons: SelectedAddonPayload[] = purchasable
                              .filter((a: any) => selectedCodes.includes(addonKey(a)))
                              .map((a: any) => ({
                                addon_code: addonKey(a),
                                name: a.add_on,
                                amount: Number(a.amount) || 0,
                              }));
                            const addonsTotal = selectedAddons.reduce((s, a) => s + a.amount, 0);
                            const packageCode = getCyclePackageCode(pkg, billingCycle);
                            onSelectPlan(
                              pkg.package_name,
                              displayPrice + addonsTotal,
                              billingCycle,
                              selectedAddons,
                              packageCode,
                            );
                          }}
                          className="w-full py-2.5 rounded-lg font-bold text-sm bg-primary-600 text-white hover:bg-primary-700 transition-colors mt-auto"
                        >
                          Select {pkg.package_name}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
